/**
 * The staff Add benchmark form on the Evals page: its defaults, and the
 * change it makes to the team's customer config row.
 *
 * The form does in one dialog what staff otherwise do across Connections,
 * Evals config, Models and Jobs: link the TrainLoop GitHub App, add a
 * GitHub benchmark, set the default model, and start the benchmark
 * inventory. Every write goes through the staff endpoints those pages
 * already use, so the api applies the same rules.
 *
 * A trainloop-env world names its own judge in `world.json`
 * (`judge.model`), and since #1170 no team setting can. The dialog shows
 * that judge read-only (`WorldJudge`) and refuses Add for a world that
 * names none, since the adapter refuses such a world everywhere.
 *
 * The config change reuses the Evals config page's form logic
 * (evalsConfigForm.ts): the row is read, the benchmark is added to it as
 * that page would add it, and the save carries the version it was read
 * at. A benchmark already on the row (same eval group slug) is kept, so a
 * second submit after a failed step does not add it twice.
 *
 * The new benchmark becomes the scored one only while the team has no
 * eval PR layout. Once a layout is set the api freezes the scored
 * repository and adapter (`_refuse_moving_the_layout`), so a benchmark
 * added after that is listed beside the scored one and not scored.
 */

import type {
  BenchmarkAdapterItem,
  PlatformGithubAppInstallation,
  TeamEvalsConfig,
  TeamEvalsConfigResponse,
  TeamModelAdminRow,
} from '@/api/generated/model'
import { newEntry, type EntryRow } from '@/components/internal/team-config/benchmarkSources'
import {
  DEFAULT_TRAINING_MAX_STEPS,
  type PresetChoices,
} from '@/components/internal/team-config/training'
import {
  emptyForm,
  formFromConfig,
  hasLayout,
  isFormDirty,
  toBody,
  trimTerms,
  validateForm,
  type EvalsConfigForm,
} from '@/components/internal/team-config/evalsConfigForm'
import { deriveSlug } from '@/lib/slug'

export const DEFAULT_ADAPTER = 'trainloop-env'
export const DEFAULT_BASE_MODEL = 'wandb/qwen3.8-27b'
/** The job the Add button starts once everything is saved. */
export const INVENTORY_JOB = 'benchmark-inventory'

/** Where a trainloop-env world keeps its tasks, and where generated ones go. */
const TRAINLOOP_ENV_LAYOUT = { testsRoot: 'tasks', testsSubdir: 'generated' } as const

export interface AddBenchmarkInput {
  /** Saved scrub settings when this draft opened, for concurrent-edit protection. */
  readonly scrubBaseline: ScrubSettings
  repo: string
  ref: string
  adapter: string
  groupDisplay: string
  testsRoot: string
  testsSubdir: string
  /** Where eval PRs link each trajectory: the platform's own address, with no field. */
  reviewBaseUrl: string
  /** The catalog model the team's default model runs on, by catalog slug. */
  baseModel: string
  modelSlug: string
  modelDisplay: string
  /** The team's nightly scoring switch, written when the benchmark becomes scored. */
  scoring: boolean
  /**
   * The team's task pipeline switch (generation, the gate and the dataset
   * build), written when the benchmark becomes scored. Off by default: the
   * gate spends on every task it proves.
   */
  generating: boolean
  /**
   * The row's deny list (client names to scrub) and its empty-list
   * acknowledgment, written with task generation on: the job waits until
   * one of the two is set (`train_loop.benchmark_prs`).
   */
  denyTerms: string[]
  ackEmptyDenyList: boolean
  /**
   * The chain's GRPO training (TrainingFields), written with task
   * generation on and off with it: the run trains what the gate proves.
   */
  training: boolean
  trainingPreset: string | null
  trainingMaxSteps: number | null
}

/** A blank form with the defaults filled in. `origin` is the platform's own address. */
export function defaultInput(origin: string): AddBenchmarkInput {
  return {
    scrubBaseline: scrubSettings(null),
    repo: '',
    ref: '',
    adapter: DEFAULT_ADAPTER,
    groupDisplay: '',
    ...TRAINLOOP_ENV_LAYOUT,
    reviewBaseUrl: origin,
    baseModel: DEFAULT_BASE_MODEL,
    modelSlug: modelSlugFor(DEFAULT_BASE_MODEL),
    modelDisplay: '',
    scoring: true,
    generating: true,
    denyTerms: [],
    ackEmptyDenyList: true,
    training: true,
    // Add sends the chain's default preset when it fits the benchmark's
    // routes (SetupForm `withChainPreset`).
    trainingPreset: null,
    trainingMaxSteps: DEFAULT_TRAINING_MAX_STEPS,
  }
}

/**
 * The form as the dialog opens it for a team: the defaults, with what the
 * team already has filled in so a second Add of its scored benchmark
 * keeps its model and training settings. Task generation starts on, and
 * an empty deny list starts allowed. Saved client names are kept. The
 * default model is the team's current one (which the model step keeps).
 */
export function initialInput(
  origin: string,
  setup: TeamEvalsConfigResponse,
  models: readonly TeamModelAdminRow[],
): AddBenchmarkInput {
  const input = defaultInput(origin)
  const current = models.find((row) => row.model.is_default)
  return {
    ...input,
    scrubBaseline: scrubSettings(setup.config),
    scoring: setup.config?.evals_enabled ?? input.scoring,
    denyTerms: [...(setup.config?.standing_deny_terms ?? input.denyTerms)],
    // Staff persist this allow-empty default only when they submit Add.
    ackEmptyDenyList: trimTerms(setup.config?.standing_deny_terms ?? []).length === 0,
    training: setup.config?.training_enabled ?? input.training,
    trainingPreset: setup.config?.training_preset ?? input.trainingPreset,
    trainingMaxSteps: setup.config?.training_max_steps ?? input.trainingMaxSteps,
    ...(current === undefined
      ? {}
      : {
          baseModel: current.forwards_to?.catalog_slug ?? '',
          modelSlug: current.model.slug,
          modelDisplay: current.model.display_name,
        }),
  }
}

/**
 * Whether the dialog shows the team model's slug and name. They apply
 * when the team's current default runs on a custom endpoint (no base
 * model), which Add keeps under its slug, and when the team already has
 * a model under the slug that runs on another base model, which Add
 * refuses (`modelConflict`). Otherwise both follow the base model.
 */
export function showsModelIdentity(
  input: AddBenchmarkInput,
  models: readonly TeamModelAdminRow[],
): boolean {
  if (input.baseModel === '') return true
  const existing = models.find((row) => row.model.slug === input.modelSlug)
  return existing !== undefined && modelConflict(existing, input.baseModel) !== null
}

/** The team model slug for a catalog model: the part after its provider. */
export function modelSlugFor(catalogSlug: string): string {
  return deriveSlug(catalogSlug.split('/').pop() ?? catalogSlug)
}

/** The eval group name a repository suggests: its name without the org. */
export function groupDisplayFor(repo: string): string {
  return repo.split('/').pop() ?? repo
}

/**
 * What the dialog knows of the judge a trainloop-env world names
 * (`world.json`, `judge.model`), read from the chosen repository and
 * branch (hooks/useAddBenchmark.ts `useWorldJudge`). `unpicked` is no
 * repository or branch yet, `unnamed` a world that names no judge (or a
 * blank one), and `unreadable` carries why the api could not read it.
 */
export type WorldJudge =
  | { kind: 'unpicked' }
  | { kind: 'reading' }
  | { kind: 'named'; model: string }
  | { kind: 'unnamed' }
  | { kind: 'unreadable'; detail: string }

/** Why Add refuses a world with no judge. Scoring and training would fail on it. */
export const WORLD_NAMES_NO_JUDGE =
  'The world.json on this branch names no judge.model. Scoring and training need one, so set judge.model in world.json first.'

/**
 * Whether the judge keeps Add off: while it is read, and for a world
 * that names none or cannot be read. There is no fallback judge. With no
 * repository or branch picked yet, the config step says what is missing.
 */
export function judgeBlocksAdd(judge: WorldJudge): boolean {
  return judge.kind !== 'named' && judge.kind !== 'unpicked'
}

/**
 * The form as Add sends it, with training on: the team's saved preset
 * when it fits this benchmark's routes, else the chain's default when
 * that fits. No preset (Add stays off) while the trainer's presets load
 * or when none fits, since the dialog has no picker.
 */
export function withChainPreset(
  input: AddBenchmarkInput,
  choices: PresetChoices | undefined,
): AddBenchmarkInput {
  if (!input.training) return input
  if (choices === undefined) return { ...input, trainingPreset: null }
  const fitting = [choices.chainDefault, ...choices.others]
  if (fitting.some((preset) => preset !== null && preset.name === input.trainingPreset)) {
    return input
  }
  return { ...input, trainingPreset: choices.chainDefault?.name ?? null }
}

/**
 * The installation the dialog links on its own: the one installation of
 * the App that was not listed when Install opened GitHub. None before
 * Install is pressed, while nothing new is listed, and when several are
 * new, since then only staff can tell which one is this team's.
 */
export function appearedInstallation(
  before: readonly number[] | null,
  now: readonly PlatformGithubAppInstallation[] | undefined,
): PlatformGithubAppInstallation | undefined {
  if (before === null || now === undefined) return undefined
  const appeared = now.filter((installation) => !before.includes(installation.installation_id))
  return appeared.length === 1 ? appeared[0] : undefined
}

/**
 * Whether the team has an eval PR layout, which freezes the scored
 * repository and adapter. Add then keeps the layout as it is.
 */
export function keepsLayout(setup: TeamEvalsConfigResponse): boolean {
  return setup.config !== null && hasLayout(formFromConfig(setup.config))
}

/**
 * Whether this benchmark is the one the night scores after Add: a team
 * with no layout scores it, and a team with a layout keeps scoring the
 * benchmark it scores now, which is this one on a second Add after a
 * refused step. Decided per benchmark, never from the layout alone: the
 * first Add sets the layout, and a retry must still finish the model step
 * for the benchmark it made scored.
 */
export function becomesScored(setup: TeamEvalsConfigResponse, input: AddBenchmarkInput): boolean {
  const loaded = setup.config === null ? emptyForm() : formFromConfig(setup.config)
  const placed = withEntry(loaded, benchmarkEntry(input))
  return !hasLayout(loaded) || (!placed.added && loaded.scored_benchmark === placed.index)
}

/**
 * Whether Add sets the team's default model: when this benchmark is the
 * scored one, or when the team has no default model at all, so the night
 * has one to score. Another scored benchmark's default model is left alone.
 */
export function setsDefaultModel(setup: TeamEvalsConfigResponse, scored: boolean): boolean {
  return scored || setup.default_model === null
}

/**
 * Why the team model with the chosen slug cannot be the default for this
 * benchmark, or null when it runs on the chosen base model. The team's
 * current default on a custom endpoint, as the dialog fills it in (no
 * base model), is kept as it is.
 */
export function modelConflict(row: TeamModelAdminRow, baseModel: string): string | null {
  const runsOn = row.forwards_to?.catalog_slug ?? null
  if (runsOn === baseModel) return null
  if (row.model.is_default && runsOn === null && baseModel === '') return null
  return `The team model ${row.model.slug} already runs on ${runsOn ?? 'a custom endpoint'}. Pick another model slug, or pick that base model.`
}

function benchmarkEntry(input: AddBenchmarkInput): EntryRow {
  const entry = newEntry()
  return {
    ...entry,
    source: { kind: 'github', repo: input.repo.trim(), ref: input.ref.trim() },
    harness: { ...entry.harness, adapter: input.adapter },
    group: { slug: deriveSlug(input.groupDisplay), display: input.groupDisplay.trim() },
  }
}

function sameBenchmark(a: EntryRow, b: EntryRow): boolean {
  if (a.source.kind !== 'github' || b.source.kind !== 'github') return false
  return (
    a.source.repo.toLowerCase() === b.source.repo.toLowerCase() &&
    a.source.ref === b.source.ref &&
    a.harness.adapter === b.harness.adapter
  )
}

/** The form with the benchmark on it: where it sits, and whether the row changed. */
function withEntry(
  form: EvalsConfigForm,
  entry: EntryRow,
): { form: EvalsConfigForm; index: number; added: boolean } {
  const index = form.benchmark_sources.findIndex((row) => row.group.slug === entry.group.slug)
  if (index !== -1) return { form, index, added: false }
  const rows = [...form.benchmark_sources, entry]
  return { form: { ...form, benchmark_sources: rows }, index: rows.length - 1, added: true }
}

/**
 * The form with the benchmark scored and the two switches as the dialog
 * sets them. With task generation on, the deny list and the training
 * settings are the dialog's too, and training is off with generation off.
 * A row with no layout takes the dialog's layout; a row with one keeps
 * it, since the api refuses moving it.
 */
function scoredForm(
  form: EvalsConfigForm,
  index: number,
  input: AddBenchmarkInput,
): EvalsConfigForm {
  const scored = {
    ...form,
    evals_enabled: input.scoring,
    task_pipeline_enabled: input.generating,
    scored_benchmark: index,
    ...(input.generating
      ? {
          standing_deny_terms: input.denyTerms,
          ack_empty_standing_terms: input.ackEmptyDenyList,
        }
      : {}),
    training_enabled: input.generating && input.training,
    ...(input.generating && input.training
      ? { training_preset: input.trainingPreset, training_max_steps: input.trainingMaxSteps }
      : {}),
  }
  if (hasLayout(form)) return scored
  return {
    ...scored,
    tests_root: input.testsRoot,
    tests_subdir: input.testsSubdir,
    review_base_url: input.testsRoot.trim() === '' ? '' : input.reviewBaseUrl,
  }
}

export const SCRUB_SETTINGS_CHANGED =
  'The client-name scrubbing settings changed while this dialog was open. Close and reopen it to review the saved settings.'

interface ScrubSettings {
  readonly terms: readonly string[]
  readonly allowEmpty: boolean
}

function scrubSettings(config: TeamEvalsConfig | null): ScrubSettings {
  return {
    terms: trimTerms(config?.standing_deny_terms ?? []),
    allowEmpty: config?.ack_empty_standing_terms ?? false,
  }
}

/** A retry may see its own saved settings; a different concurrent change must be reviewed. */
function scrubSettingsConflict(setup: TeamEvalsConfigResponse, input: AddBenchmarkInput): boolean {
  const latest = JSON.stringify(scrubSettings(setup.config))
  const intended: ScrubSettings = {
    terms: trimTerms(input.denyTerms),
    allowEmpty: input.ackEmptyDenyList,
  }
  return latest !== JSON.stringify(input.scrubBaseline) && latest !== JSON.stringify(intended)
}

export type ConfigChange =
  | { kind: 'save'; body: TeamEvalsConfig; scored: boolean }
  | { kind: 'unchanged'; scored: boolean }
  | { kind: 'refused'; reason: string }

/**
 * What the save sends for this benchmark, given the row as it was read.
 * When the benchmark is the scored one (`becomesScored`) the row takes the
 * dialog's scoring and task pipeline switches; otherwise the row keeps
 * its own. `unchanged` is a row that already holds what the save would
 * write, so a repeat submit saves nothing. `refused` carries the Evals
 * config page's own validation message, names the benchmark already in
 * the eval group when another repository, branch or adapter fills it, or
 * says the client-name scrubbing changed while the dialog was open. The
 * dialog always sends a layout (trainloop-env's) and a deny list (the
 * saved names, or an allowed empty list), so task generation never waits
 * on either.
 */
export function configChange(
  setup: TeamEvalsConfigResponse,
  input: AddBenchmarkInput,
  adapters: readonly BenchmarkAdapterItem[],
): ConfigChange {
  const loaded = setup.config === null ? emptyForm() : formFromConfig(setup.config)
  const entry = benchmarkEntry(input)
  const placed = withEntry(loaded, entry)
  const held = placed.form.benchmark_sources[placed.index]
  if (!placed.added && held !== undefined && !sameBenchmark(held, entry)) {
    const from = held.source.kind === 'github' ? held.source.repo : 'an S3 prefix'
    return {
      kind: 'refused',
      reason: `The eval group ${entry.group.slug} already holds ${from}. Give this benchmark another eval group name.`,
    }
  }
  const scored = becomesScored(setup, input)
  if (scored && input.generating && scrubSettingsConflict(setup, input)) {
    return { kind: 'refused', reason: SCRUB_SETTINGS_CHANGED }
  }
  const form = scored ? scoredForm(placed.form, placed.index, input) : placed.form
  if (!isFormDirty(loaded, form)) return { kind: 'unchanged', scored }
  const reason = validateForm(form, adapters)
  if (reason !== null) return { kind: 'refused', reason }
  return { kind: 'save', body: toBody(form), scored }
}
